import { Fragment, useCallback, useEffect, useId, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  knowledgeApi,
  type CustomerBelief,
  type LeastSureCount,
  type LeastSure as LeastSureData,
  type LeastSureItem,
  type LeastSureScopeBound,
  type LeastSureSubject,
  type ProductBelief,
} from '../../services/api'
import {
  Avatar,
  Badge,
  Button,
  Card,
  EntryList,
  EntryRow,
  ErrorState,
  Hint,
  LoadingState,
  Meta,
  Meter,
  Section,
  Stack,
} from '../../ds'
import { VERDICT_LABEL, reverseVerdict, settlementLine, type Verdict } from './BeliefRows'
import { refusalFor } from './refusal'
import {
  beliefItemKey,
  confidenceRange,
  evidenceLineParts,
  evidenceReceiptTo,
  foldSameBelief,
  formedDate,
  leastSureBeliefId,
  typeLabel,
  type LeastSureEntry,
} from './leastSureFold'

/**
 * "One customer at a time" (MAR-401; was "Where she is least sure") — Knowledge v6’s idle state (spec §3.2, MAR-325).
 *
 * One row per SUBJECT (a person or a product), ascending by the confidence of that
 * subject's least-sure active belief, on the one scale both subjects share. The order is
 * the server's and is rendered as given.
 *
 * ── What each row claims ────────────────────────────────────────────────────────────────
 *
 * ⚠️ The % is ONE BELIEF's own confidence — `reason.belief` is that belief, and its sentence
 * is the row's reason. There is no person-level or product-level confidence (spec decisions
 * 3–4), so the copy says "N% sure" next to the belief it belongs to and never "N% known".
 * The bar is one colour at every value: colouring it by band would be a threshold, and the
 * Judgment test says there are none here — only read bounds, which are stated below it.
 *
 * ⚠️ A person whose contact details could not be decrypted arrives as `label: null` with
 * `labelUnavailable`. The row is real — only the name is withheld — so it says so in words
 * and is never rendered as blank or as "anonymous".
 *
 * ── A short task, not a report (MAR-383) ────────────────────────────────────────────────
 *
 * Each row carries its verdicts inline (Confirm / Not true, `useLeastSureVerdicts` — the
 * drawer's routes and token rule, held by the page so they outlive this list) and one plain-text "From …" line from the belief's first
 * evidence item. The row is a STATIC line whose name opens the record, because a row that is
 * a `<button>` may hold no other control. The subject chip shows only on a mixed list; under
 * a scope it repeated the scope. The visible "How sure" label is gone — the bar and its
 * number carry it. The read's window sits under the title as its scope. Rows with the same
 * belief type and EXACT wording formed on one day fold into one (`foldSameBelief`), which
 * opens one by one: each member keeps its own verdict.
 *
 * ── The v6 anatomy (MAR-401) ────────────────────────────────────────────────────────────
 *
 * "One customer at a time" / "One product at a time", least sure first, with the window under
 * the title and the verdict hint beside it. The "N to check" count is NOT here: it is the To
 * check tab's badge (`Knowledge.tsx`), from the same `countText`. A product row leads with its
 * initial tile and its belief's type. A belief the pattern cards above already list (`exclude`,
 * by `beliefItemKey`) is not listed again, and a footer says how many were left out — so the
 * page never shows one belief twice. "formed" drops the year when it is this one.
 *
 * ── Three states, and they are three ────────────────────────────────────────────────────
 *
 * loading · failed · loaded. A failed read is a 500 (the server never answers a dead read
 * with an empty list), and it renders as a failure with a retry — never as the empty state.
 * An EMPTY loaded list is honest and ordinary: before `product-belief-synthesizer`
 * (MAR-324) is deployed there are no product beliefs at all, and a new store has no
 * customer beliefs yet. It must not read as an error.
 *
 * ── One read per scope ──────────────────────────────────────────────────────────────────
 *
 * The page's scope chip picks which subjects are listed: Customers shows only people,
 * Products only products. Each is its OWN server read — `?scope=customer|product&limit=10`
 * — so the server's `limit`, verify cap and `exhaustive` describe exactly the list shown,
 * and the other subject can neither spend its budget nor appear in it. A scope is read
 * only when its chip is shown, once per (workspace, scope), and kept. With no `scope` the
 * hook makes the unscoped call and the list is mixed, as the server orders it.
 *
 * ── The bound ───────────────────────────────────────────────────────────────────────────
 *
 * `bound` is the server's statement of how far the read went, and `exhaustive` is its claim
 * that nothing unread could displace the answer. This file states it, never infers it from
 * `items.length`: the window's start date, and a note for each reason a scope is not
 * exhaustive — candidates that could not be checked (`failed`), products not read at all
 * (`unavailable`), or a read that stopped at its window or verification cap.
 */

/** One read, stamped with the workspace and scope it describes, matched at the read. */
interface Snapshot {
  workspaceId: string
  scope: LeastSureSubject | null
  state: 'loading' | 'failed' | 'loaded'
  data: LeastSureData | null
}

const slot = (workspaceId: string, scope: LeastSureSubject | null) => JSON.stringify([workspaceId, scope])

export interface LeastSureHandle {
  /** `idle` = no workspace, so nothing was read and nothing is claimed. */
  state: 'idle' | 'loading' | 'failed' | 'loaded'
  data: LeastSureData | null
  reload: () => void
}

/** How many subjects one read asks for — the list's length, per scope. */
export const LEAST_SURE_LIMIT = 10

/**
 * The least-sure read for one (workspace, scope). `enabled` defers it until the view that
 * renders it is on screen; each (workspace, scope) is requested once and kept, so switching
 * chips back and forth costs nothing (a retry is `reload`). `scope` omitted = the unscoped,
 * mixed read.
 *
 * ⚠️ One snapshot PER (workspace, scope), each stamped and matched at the read: a slow
 * Products read must never land as the Customers list, nor one workspace's as another's.
 */
export function useLeastSure(
  workspaceId: string | undefined,
  scope?: LeastSureSubject,
  enabled = true
): LeastSureHandle {
  const sc = scope ?? null
  const [snaps, setSnaps] = useState<Record<string, Snapshot>>({})
  /** Newest request per slot: only it may write that slot. */
  const request = useRef<Record<string, number>>({})
  /** Slots already requested. */
  const requested = useRef<Set<string>>(new Set())

  const load = useCallback(async() => {
    if (!workspaceId) return
    const key = slot(workspaceId, sc)
    const mine = (request.current[key] = (request.current[key] ?? 0) + 1)
    const write = (s: Snapshot) => {
      if (mine === request.current[key]) setSnaps((all) => ({ ...all, [key]: s }))
    }
    write({ workspaceId, scope: sc, state: 'loading', data: null })
    try {
      const res = await knowledgeApi.getLeastSure(workspaceId, LEAST_SURE_LIMIT, sc ?? undefined)
      // ⚠️ A response with no `items` array is not "nothing to doubt": it is a shape this
      // screen cannot read, and calling it empty would be the quiet failure.
      if (!res.success || !res.data || !Array.isArray(res.data.items) || !res.data.bound) {
        throw new Error('least_sure_unavailable')
      }
      // ⚠️ Fail closed on a server that did not APPLY `?scope=`. A knowledge-api that
      // predates it ignores the param and answers the mixed list — in a deploy window, after
      // a rollback, or after a deploy that did not land — and the Products chip would then
      // silently list people under a bound that describes both. The echo is the proof; with
      // none, or the wrong one, this is a failed read with a retry. The unscoped call asks
      // for nothing and checks nothing.
      if (sc && res.data.bound.scope !== sc) throw new Error('least_sure_scope_not_applied')
      write({ workspaceId, scope: sc, state: 'loaded', data: res.data })
    } catch {
      write({ workspaceId, scope: sc, state: 'failed', data: null })
    }
  }, [workspaceId, sc])

  useEffect(() => {
    if (!workspaceId || !enabled) return
    const key = slot(workspaceId, sc)
    if (requested.current.has(key)) return
    requested.current.add(key)
    void load()
  }, [load, workspaceId, sc, enabled])

  const found = workspaceId ? snaps[slot(workspaceId, sc)] : undefined
  const current = found && found.workspaceId === workspaceId && found.scope === sc ? found : null
  return {
    // Not yet asked for (no workspace, or the view is not on screen): nothing is claimed.
    state: !workspaceId || (!current && !enabled) ? 'idle' : (current?.state ?? 'loading'),
    data: current?.data ?? null,
    reload: () => {
      void load()
    },
  }
}

/** The name a row is about — never blank, never a belief. */
export function leastSureName(item: LeastSureItem): string {
  if (item.scope === 'customer') {
    if (!item.label) {
      return item.labelUnavailable
        ? 'Name unavailable — their contact details could not be read'
        : 'A customer with no name on record'
    }
    const l = item.label as { firstName: string | null; lastName: string | null; emailMasked: string | null }
    return `${l.firstName || ''} ${l.lastName || ''}`.trim() || l.emailMasked || 'A customer with no name on record'
  }
  const title = (item.label as { title: string | null } | null)?.title
  return title || `Product ${item.id} — no title on record`
}

/** The window's start — with its year: "since" reads as a span, and a span needs both ends. */
const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return null
  const d = new Date(iso)
  if (isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
}

export type LeastSureScope = LeastSureSubject

/**
 * The "To check" number as the server stated it (MAR-389): `N` when exact, `N+` for a floor,
 * `null` when the server said nothing (one that predates the count) — never a number inferred
 * from `items.length`, which is capped by `limit`.
 */
export function countText(count: LeastSureCount | undefined | null): string | null {
  if (typeof count === 'number' && Number.isFinite(count)) return String(count)
  if (count && typeof count === 'object' && typeof count.atLeast === 'number' && Number.isFinite(count.atLeast)) {
    return `${count.atLeast}+`
  }
  return null
}

/** The oldest generation the scopes' windows saw — where "since" starts. */
function windowStart(b: LeastSureData['bound'], only?: LeastSureScope): string | null {
  const scopes = only === 'customer' ? [b.customers] : only === 'product' ? [b.products] : [b.customers, b.products]
  const froms = scopes.map((s) => s?.windowFrom).filter((x): x is string => !!x)
  if (froms.length === 0) return null
  return froms.sort()[0]
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

/**
 * Why the list may not be the whole answer, one sentence per reason the server gave.
 * Empty when the read was exhaustive — the server's claim, not this file's.
 *
 * `only` narrows it to one scope's bound: a list of customers says nothing about why
 * products could not be read.
 */
export function boundNotes(b: LeastSureData['bound'], only?: LeastSureScope): string[] {
  const notes: string[] = []
  const all: [LeastSureScope, LeastSureScopeBound | undefined, string, string][] = [
    ['customer', b.customers, 'customer', 'customers'],
    ['product', b.products, 'product', 'products'],
  ]
  const scopes = all.filter(([k]) => !only || k === only)
  if ((!only || only === 'product') && b.products?.unavailable) {
    notes.push('Products could not be read just now, so none of them are listed here.')
  }
  for (const [, s, one, many] of scopes) {
    if (s && s.failed > 0) {
      notes.push(`${plural(s.failed, one, many)} could not be checked, so someone less sure may be missing.`)
    }
  }
  // The read stopped at its window (older generations unseen) or at its verification cap
  // with candidates still unread. With no failure and no `unavailable`, a non-exhaustive
  // scope can only mean that. ⚠️ A scope with failures is non-exhaustive BECAUSE of them, so
  // `exhaustive` alone cannot say it was ALSO cut off; the bound still shows it when
  // candidates went unattempted (`verified + failed < candidates`) or the window used its
  // page budget (`pages >= maxWindowPages`) — and then both notes are said, not one.
  const cutOff = (s: LeastSureScopeBound) =>
    s.verified + s.failed < s.candidates || s.pages >= b.maxWindowPages
  const stoppedShort = scopes.some(
    ([, s]) => s && !s.exhaustive && !s.unavailable && (!(s.failed > 0) || cutOff(s))
  )
  // ⚠️ `catalogueUnverified`, `malformed`, `unresolved` and `skipped` are deliberately NOT
  // shown: none of them makes a scope non-exhaustive (each is a row read and accounted for —
  // kept on the card's proof, never a candidate's estimate, or dropped as not this store's),
  // so they say nothing about whether someone less sure is missing. They are on the bound
  // and in the server's logs for whoever needs them.
  if (stoppedShort) {
    notes.push('She stopped before reading every belief, so one she is less sure of may not be listed.')
  }
  // The flag for what is shown disagrees with every reason above: say so rather than claim
  // completeness. One scope's own flag when narrowed; the overall one otherwise.
  // ⚠️ Fail closed: a scope with NO entry was not stated by the server, so it is not called
  // exhaustive — the note is said rather than completeness assumed.
  const scopeExhaustive = only ? scopes.every(([, s]) => !!s && s.exhaustive) : b.exhaustive
  if (notes.length === 0 && !scopeExhaustive) {
    notes.push('This may not be every belief she holds.')
  }
  return notes
}

/** The empty state, said only as far as the read proves it. */
function emptyCopy(data: LeastSureData, scope: LeastSureScope | undefined): string {
  if (!scope) {
    return 'Nothing to doubt yet. Each belief she forms about a customer or a product appears here with how sure she is of it, least sure first.'
  }
  const noun = scope === 'customer' ? 'customer' : 'product'
  const s = scope === 'customer' ? data.bound.customers : data.bound.products
  const clean = !!s && s.exhaustive && !s.unavailable && !(s.failed > 0)
  if (!clean) return `No ${noun} beliefs are listed.`
  return scope === 'customer'
    ? 'No customer beliefs have formed yet. Each belief she forms about a customer appears here with how sure she is of it, least sure first.'
    : 'She hasn’t formed a view on any product yet. She needs several customers’ orders, returns or questions about one before she does.'
}

// ── Inline verdicts (MAR-383) ───────────────────────────────────────────────────────────

type LeastSureBelief = CustomerBelief | ProductBelief

/** The last verdict attempt on one row, scoped to the workspace, subject and belief it was fired on. */
interface RowVerdict {
  /** The belief as the verdict route last returned it — the row settles in place from this. */
  belief: LeastSureBelief | null
  /** Which verdict is in flight, so only the pressed button says "Recording…". */
  busy: Verdict | null
  /**
   * The verdict landed but the route returned no row, so `belief` carries the status and an
   * `updatedAt` the server has already moved past. No token on screen can match, so the row
   * offers nothing until a re-read hands back the row as it now stands.
   */
  stale: boolean
  note: { tone: 'ok' | 'bad'; text: string } | null
}

const verdictKey = (workspaceId: string, item: LeastSureItem, beliefId: string) =>
  JSON.stringify([workspaceId, item.scope, item.id, beliefId])

/**
 * What one verdict attempt came to — so a caller firing several (the pattern's "Right for all
 * N", MAR-389) can say how many landed rather than assume they all did.
 *  - `recorded`: 200, settled or already settled;
 *  - `refused`: the route refused it (409, 404, …) or the request failed — the row says why;
 *  - `skipped`: nothing was sent (no workspace, no token that can match, one already in flight).
 */
export type VerdictOutcome = 'recorded' | 'refused' | 'skipped'

export interface LeastSureVerdicts {
  /** The belief this row should show now, and the last attempt's busy flag and note. */
  rowFor: (item: LeastSureItem) => {
    belief: LeastSureBelief
    busy: Verdict | null
    stale: boolean
    note: RowVerdict['note']
  }
  /** `null` when the row cannot be settled here: no workspace, or a belief with no id. */
  settle: ((item: LeastSureItem, verdict: Verdict) => Promise<VerdictOutcome>) | null
}

/**
 * The verdict on a least-sure row, through the SAME routes the drawer's `BeliefRows` uses:
 * `settleBelief` for a person (`hypothesisId`), `settleProductBelief` for a product
 * (`beliefId`), with `expectedUpdatedAt` EXACTLY as the row's belief carried it — never
 * re-derived (see the note on `BeliefRows`' `settle`). The three outcomes are read the same
 * way: 200 settled, 200 `alreadySettled` (a success), and 409 with `details.current`, which
 * re-renders the row in place so the next press carries a token that can match.
 *
 * ⚠️ A settled row stays where it was. The list is the server's, read once; removing the row
 * the merchant just answered would make their own action look like it vanished.
 *
 * ⚠️ Call this ABOVE anything that unmounts `<LeastSure>` (the page, beside `useLeastSure`):
 * the least-sure snapshot outlives the list's mount, so verdicts held inside it would be
 * forgotten on a trip to Raw events or another tab and the row would come back unsettled on its old token.
 */
export function useLeastSureVerdicts(workspaceId: string | undefined): LeastSureVerdicts {
  const [rows, setRows] = useState<Record<string, RowVerdict>>({})
  /** Keys with a POST in flight — a second press must not send a token the first superseded. */
  const inFlight = useRef<Set<string>>(new Set())

  const rowFor = useCallback(
    (item: LeastSureItem) => {
      const served = item.reason.belief
      const id = leastSureBeliefId(item.scope, served)
      const state = workspaceId && id ? rows[verdictKey(workspaceId, item, id)] : undefined
      // The newer of the two wins: a re-read that moved past our verdict is the truth.
      const mine = state?.belief
      const belief = mine && String(mine.updatedAt ?? '') >= String(served.updatedAt ?? '') ? mine : served
      return {
        belief,
        busy: state?.busy ?? null,
        stale: !!state?.stale && belief === mine,
        note: state?.note ?? null,
      }
    },
    [rows, workspaceId]
  )

  /**
   * Re-read ONE subject's beliefs (settled included) and take this belief as it now stands —
   * the only way a row the verdict route returned without a body gets a token that can match.
   * Not found or unreadable: the row stays stale (no controls) and says where to change it.
   */
  const reread = useCallback(
    async(item: LeastSureItem, id: string, key: string) => {
      if (!workspaceId) return
      let found: LeastSureBelief | null = null
      try {
        const res =
          item.scope === 'customer'
            ? await knowledgeApi.getCustomerKnowledge(workspaceId, item.id, { includeSettled: true })
            : await knowledgeApi.getProductKnowledge(workspaceId, item.id, { includeSettled: true })
        const list = (res.success && res.data?.beliefs) || []
        found = (list as LeastSureBelief[]).find((b) => leastSureBeliefId(item.scope, b) === id) ?? null
      } catch {
        found = null
      }
      setRows((all) => {
        const cur = all[key]
        if (!cur) return all
        return {
          ...all,
          [key]: found
            ? { ...cur, belief: found, stale: false }
            : { ...cur, note: { tone: 'ok', text: 'Recorded. Open the record to change it.' } },
        }
      })
    },
    [workspaceId]
  )

  const settle = useCallback(
    async(item: LeastSureItem, verdict: Verdict): Promise<VerdictOutcome> => {
      if (!workspaceId) return 'skipped'
      const shown = rowFor(item)
      // A row with no token that can match offers nothing; this is the belt to that brace.
      if (shown.stale) return 'skipped'
      const belief = shown.belief
      const id = leastSureBeliefId(item.scope, item.reason.belief)
      if (!id) return 'skipped'
      const key = verdictKey(workspaceId, item, id)
      if (inFlight.current.has(key)) return 'skipped'
      inFlight.current.add(key)
      const patch = (p: Partial<RowVerdict>) =>
        setRows((all) => ({
          ...all,
          [key]: { ...(all[key] ?? { belief: null, busy: null, stale: false, note: null }), ...p },
        }))
      patch({ busy: verdict, note: null })
      const body = { verdict, expectedUpdatedAt: belief.updatedAt }
      try {
        const res =
          item.scope === 'customer'
            ? await knowledgeApi.settleBelief(workspaceId, item.id, id, body)
            : await knowledgeApi.settleProductBelief(workspaceId, item.id, id, body)
        if (res.success && res.data) {
          const returned = res.data.belief as LeastSureBelief | null
          // A success with no row in it still settled: say so on the row, without inventing a
          // source — but its `updatedAt` is the one we just spent, so the row is STALE until
          // a re-read returns the row as it now stands (`reread`, below).
          patch({
            belief: returned ?? { ...belief, status: verdict },
            stale: !returned,
            note: {
              tone: 'ok',
              text: res.data.alreadySettled
                ? 'Already recorded — she had this one settled.'
                : verdict === 'refuted'
                  ? 'Recorded. She won’t form it again from the same evidence.'
                  : 'Recorded.',
            },
          })
          if (!returned) await reread(item, id, key)
          return 'recorded'
        }
        const details = res.details as { reason?: unknown; current?: LeastSureBelief } | undefined
        patch({
          ...(details?.reason === 'hypothesis_write_conflict' && details.current ? { belief: details.current } : {}),
          note: { tone: 'bad', text: refusalFor(res, 'Nothing was recorded.') },
        })
        return 'refused'
      } catch {
        patch({ note: { tone: 'bad', text: 'Nothing was recorded. Try again.' } })
        return 'refused'
      } finally {
        inFlight.current.delete(key)
        patch({ busy: null })
      }
    },
    [workspaceId, rowFor, reread]
  )

  return { rowFor, settle: workspaceId ? settle : null }
}

// ── Rows ────────────────────────────────────────────────────────────────────────────────

/** What a row's % reads as: a live theory's own number, or a settled one's history. */
function sureText(belief: LeastSureBelief, fallback: number): { pct: number; text: string } {
  const c = belief.confidence ?? fallback
  const pct = Math.round(Math.max(0, Math.min(1, c)) * 100)
  const live = belief.status === 'active' || belief.status == null
  return { pct, text: live ? `${pct}% sure` : `was ${pct}% sure` }
}

/**
 * The evidence line, said in words when she recorded none — never a blank. The item is a
 * link only when the server attached a receipt to it (MAR-390); otherwise plain text.
 */
export function FromLine({ belief }: { belief: LeastSureBelief }) {
  const parts = evidenceLineParts(belief)
  if (!parts) return <>She recorded no evidence for this one.</>
  const to = evidenceReceiptTo(belief, parts.index)
  return (
    <>
      From{' '}
      {to ? (
        <Link to={to} data-testid="kn-ls-from-link">
          {parts.text}
          <span className="mv-sr"> — open receipt</span>
        </Link>
      ) : (
        parts.text
      )}
    </>
  )
}

/**
 * One subject's row. A STATIC line — the name opens the record, and the verdicts sit in the
 * meta column — because a row that is itself a `<button>` may hold no other control.
 */
export function LeastSureRow({
  item,
  showTag,
  verdicts,
  onOpen,
}: {
  item: LeastSureItem
  /** The subject chip. Only on a mixed list: under a scope it repeats the scope. */
  showTag: boolean
  verdicts: LeastSureVerdicts
  onOpen: (item: LeastSureItem) => void
}) {
  const { belief, busy, stale, note } = verdicts.rowFor(item)
  const sentenceId = `kn-ls-belief-${useId()}`
  // The served row's number is the item's own (the server's `confidenceOf`); only a belief
  // the verdict route handed back carries a newer one.
  const { pct, text } = sureText(
    belief === item.reason.belief ? { ...belief, confidence: item.confidence } : belief,
    item.confidence
  )
  const formed = formedDate(item.freshness)
  const settled = belief.status === 'confirmed' || belief.status === 'refuted' || belief.status === 'expired'
  const reverse = reverseVerdict(belief)
  // A stale row's token cannot match, so it offers no verdict until a re-read lands.
  const settle = stale ? null : verdicts.settle
  const anyBusy = busy !== null
  return (
    <EntryRow
      testId="kn-ls-row"
      data-scope={item.scope}
      data-status={belief.status || 'active'}
      title={
        <>
          {/* A product leads with its initial tile (MAR-401); the name beside it says who. */}
          {item.scope === 'product' && (
            <>
              <Avatar size="sm" shape="square" name={productInitialOf(item)} label={false} />{' '}
            </>
          )}
          <Button variant="link" data-testid="kn-ls-open" onClick={() => onOpen(item)}>
            {leastSureName(item)}
          </Button>
        </>
      }
      tag={
        showTag || item.scope === 'product' ? (
          <>
            {showTag && <Badge>{item.scope === 'customer' ? 'Customer' : 'Product'}</Badge>}
            {/* A product row says what KIND of belief it is: six kinds, said in words. */}
            {item.scope === 'product' && <Badge data-testid="kn-ls-type">{typeLabel(belief.type)}</Badge>}
          </>
        ) : undefined
      }
      why={<span id={sentenceId}>{belief.hypothesis || 'A belief she holds with no wording recorded.'}</span>}
      clamp={false}
      aside={
        <>
          {/* The bar and its number carry "how sure"; the label is for a screen reader only. */}
          <Meter
            layout="inline"
            size="sm"
            label={<span className="mv-sr">How sure</span>}
            value={pct}
            valueText={text}
            muted={settled}
          />
          {formed && <Meta data-testid="kn-ls-fresh">formed {formed}</Meta>}
          {settled ? (
            <span data-testid="kn-ls-settled">
              <Meta>{settlementLine(belief)}</Meta>
              {/* ⚠️ Not "Undo": there is no way back to `active` (the verdicts are refuted and
                  confirmed only), so the reverse is the OTHER verdict and says so — the
                  drawer's wording. */}
              {settle && reverse && (
                <>
                  {' '}
                  <Button
                    variant="link"
                    data-testid="kn-ls-reverse"
                    aria-describedby={sentenceId}
                    disabled={anyBusy}
                    onClick={() => void settle(item, reverse)}
                  >
                    {busy === reverse ? 'Recording…' : VERDICT_LABEL[reverse].reverse}
                  </Button>
                </>
              )}
            </span>
          ) : (
            settle && (
              <span data-testid="kn-ls-verdicts">
                <Button
                  variant="ghost"
                  size="sm"
                  aria-describedby={sentenceId}
                  disabled={anyBusy}
                  onClick={() => void settle(item, 'refuted')}
                >
                  {busy === 'refuted' ? 'Recording…' : 'Not true'}
                </Button>{' '}
                <Button
                  variant="sage"
                  size="sm"
                  aria-describedby={sentenceId}
                  disabled={anyBusy}
                  onClick={() => void settle(item, 'confirmed')}
                >
                  {busy === 'confirmed' ? 'Recording…' : 'Confirm'}
                </Button>
              </span>
            )
          )}
          {note && (
            <Meta role="status" data-testid="kn-ls-verdict-note" data-tone={note.tone}>
              {note.text}
            </Meta>
          )}
        </>
      }
    >
      <span data-testid="kn-ls-from">
        <FromLine belief={belief} />
      </span>
    </EntryRow>
  )
}

/** The title a product's tile takes its initial from — empty (a blank tile) when there is none. */
function productInitialOf(item: LeastSureItem): string {
  return (item.label as { title: string | null } | null)?.title ?? ''
}

/** Names for a group's title: up to three, then how many more. */
function groupNames(items: LeastSureItem[]): string {
  const names = items.map(leastSureName)
  return names.length <= 3 ? names.join(', ') : `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`
}

/**
 * "The same belief about N customers" on one day, folded into one line (`foldSameBelief`).
 * Each member keeps its own verdict — there is no batch route — so "One by one" opens the
 * group into its members' own rows, directly under it.
 *
 * ⚠️ No evidence line on the header: evidence is per member, and one member's "From …"
 * printed over the group would claim it for all of them. Each member's row carries its own.
 */
function LeastSureGroup({
  entry,
  open,
  onToggle,
}: {
  entry: Extract<LeastSureEntry, { kind: 'group' }>
  open: boolean
  onToggle: () => void
}) {
  const first = entry.items[0]
  const noun = first.scope === 'customer' ? 'customers' : 'products'
  const formed = formedDate(first.freshness)
  const pct = Math.round(Math.max(0, Math.min(1, Math.min(...entry.items.map((i) => i.confidence)))) * 100)
  return (
    <EntryRow
      testId="kn-ls-group"
      data-scope={first.scope}
      data-count={entry.items.length}
      title={`Same belief about ${entry.items.length} ${noun} · ${groupNames(entry.items)}`}
      why={first.reason.belief.hypothesis}
      clamp={false}
      aside={
        <>
          <Meter
            layout="inline"
            size="sm"
            label={<span className="mv-sr">How sure</span>}
            value={pct}
            valueText={confidenceRange(entry.items)}
          />
          {formed && <Meta>formed {formed}</Meta>}
          <Button variant="link" aria-expanded={open} data-testid="kn-ls-group-toggle" onClick={onToggle}>
            {open ? 'Fold them' : 'One by one'}
          </Button>
        </>
      }
    />
  )
}

export default function LeastSure({
  read,
  scope,
  onOpen,
  workspaceId,
  verdicts,
  exclude,
}: {
  read: LeastSureHandle
  /** Which subjects the read was for — the page's scope chip. Omitted: a mixed list. */
  scope?: LeastSureScope
  onOpen: (item: LeastSureItem) => void
  /** The workspace the list belongs to — scopes which folded groups are open. */
  workspaceId?: string
  /**
   * The inline verdicts, held by the PAGE (`useLeastSureVerdicts`) so they outlive this
   * list's mount. Without them (or with no workspace) rows offer no verdict.
   */
  verdicts: LeastSureVerdicts
  /**
   * Beliefs the pattern cards above already list (`beliefItemKey`), so they are not listed
   * again here (MAR-401). Omitted, or before the pattern read lands: nothing is left out.
   */
  exclude?: ReadonlySet<string>
}) {
  /** Groups opened "one by one", keyed by workspace too so another store's never opens. */
  const [opened, setOpened] = useState<ReadonlySet<string>>(() => new Set())
  if (read.state === 'idle') return null
  const data = read.data

  let body: React.ReactNode
  let description: React.ReactNode
  let hint: React.ReactNode
  if (read.state === 'loading') {
    body = <LoadingState caption="Reading where she is least sure…" />
  } else if (read.state === 'failed' || !data) {
    body = (
      <ErrorState
        title="She couldn’t read where she is least sure just now. This is not an empty list — nothing is shown rather than a list that looks complete."
        onRetry={read.reload}
      />
    )
  } else {
    // The server scoped the read, and the hook refused any response that did not say so;
    // the filter is belt and braces, never the mechanism.
    const scoped = scope ? data.items.filter((i) => i.scope === scope) : data.items
    // A belief a pattern card already lists is not listed again (MAR-401).
    const items = exclude?.size
      ? scoped.filter((i) => {
        const k = beliefItemKey(i)
        return !k || !exclude.has(k)
      })
      : scoped
    const inPatterns = scoped.length - items.length
    const since = fmtDate(windowStart(data.bound, scope))
    const notes = boundNotes(data.bound, scope)
    const entries = foldSameBelief(items)
    const openKey = (key: string) => JSON.stringify([workspaceId ?? null, key])
    // The read's window, under the title rather than orphaned below the list.
    description = (
      <span data-testid="kn-ls-since">
        Least sure first{since ? ` · beliefs since ${since}` : ''}
      </span>
    )
    hint =
      items.length > 0 && verdicts.settle ? (
        <Hint data-testid="kn-ls-hint">
          A yes keeps it as yours; a no means she won’t form it again from the same evidence.
        </Hint>
      ) : undefined
    body = (
      <>
        {scoped.length === 0 ? (
          // Plain copy, not an EmptyState: its dashed box would be a border inside the card's (MAR-412).
          <p className="t-small" data-testid="kn-ls-empty">
            {emptyCopy(data, scope)}
          </p>
        ) : items.length === 0 ? null : (
          <EntryList label="Beliefs, least sure first">
            {entries.map((entry) => {
              if (entry.kind === 'one') {
                return (
                  <LeastSureRow
                    key={`${entry.item.scope}:${entry.item.id}`}
                    item={entry.item}
                    showTag={!scope}
                    verdicts={verdicts}
                    onOpen={onOpen}
                  />
                )
              }
              const k = openKey(entry.key)
              const isOpen = opened.has(k)
              return (
                <Fragment key={entry.key}>
                  <LeastSureGroup
                    entry={entry}
                    open={isOpen}
                    onToggle={() =>
                      setOpened((prev) => {
                        const next = new Set(prev)
                        if (next.has(k)) next.delete(k)
                        else next.add(k)
                        return next
                      })
                    }
                  />
                  {isOpen &&
                    entry.items.map((item) => (
                      <LeastSureRow
                        key={`${item.scope}:${item.id}`}
                        item={item}
                        showTag={!scope}
                        verdicts={verdicts}
                        onOpen={onOpen}
                      />
                    ))}
                </Fragment>
              )
            })}
          </EntryList>
        )}
        {inPatterns > 0 && (
          <Hint data-testid="kn-ls-in-patterns">
            {inPatterns === 1
              ? 'One belief that is in the patterns above is not repeated here.'
              : `${inPatterns} beliefs that are in the patterns above are not repeated here.`}
          </Hint>
        )}
        {notes.map((n) => (
          <Hint data-testid="kn-ls-note" key={n}>
            {n}
          </Hint>
        ))}
      </>
    )
  }

  // On the surface, in a bordered card, as the canvas draws the list (MAR-401). The Stack is
  // what zeroes a first Section's own 30px top margin, which in a card read as empty padding (MAR-412).
  return (
    <Card data-testid="kn-ls-card">
      <Stack>
        <Section
          title={scope === 'product' ? 'One product at a time' : scope === 'customer' ? 'One customer at a time' : 'One at a time'}
          description={description}
          aside={hint}
        >
          {body}
        </Section>
      </Stack>
    </Card>
  )
}
